import { registerPlugin, Capacitor } from '@capacitor/core';

const Storefront = registerPlugin('Storefront');

/**
 * Where a paid digital upgrade may be sold, and how it may be talked about.
 *
 * Apple's anti-steering rule (Guideline 3.1.1) forbids sending customers to an
 * outside purchase for digital content. Since 1 May 2025 — after the Epic v.
 * Apple contempt ruling — apps on the US storefront are exempt: they may show
 * links and buttons to external purchases with no commission. That exemption is
 * US-only. Canada, our main market, still sits under the original rule.
 *
 * Three outcomes:
 *   'in-app'   the web app itself — sell normally, no restriction applies
 *   'link-out' native on a storefront where external links are permitted
 *   'notice'   native everywhere else — say the account isn't active, and do
 *              NOT link, button, or otherwise route the user to a purchase
 */
export const PURCHASE_IN_APP = 'in-app';
export const PURCHASE_LINK_OUT = 'link-out';
export const PURCHASE_NOTICE = 'notice';

/** Storefronts where Apple permits linking out for digital goods. Alpha-3. */
const LINK_OUT_ALLOWED = new Set(['USA']);

/**
 * Pure decision, exported for tests.
 *
 * `country` is an App Store storefront code, not a device region — and null
 * whenever StoreKit couldn't tell us. Unknown resolves to 'notice': being
 * wrong in the permissive direction is what gets an app rejected, so silence
 * is the safe default.
 */
export function purchaseModeFor({ isNative, country }) {
  if (!isNative) return PURCHASE_IN_APP;
  if (country && LINK_OUT_ALLOWED.has(country.toUpperCase())) return PURCHASE_LINK_OUT;
  return PURCHASE_NOTICE;
}

/** Resolve the mode for the running app. Never throws. */
export async function getPurchaseMode() {
  if (!Capacitor.isNativePlatform()) return PURCHASE_IN_APP;
  let country = null;
  try {
    const res = await Storefront.getCountry();
    country = res?.country ?? null;
  } catch {
    // Plugin missing (Android, or an older build) — fall through to the safe default.
  }
  return purchaseModeFor({ isNative: true, country });
}
